import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataModeSelector } from '@/components/ui/data-mode-selector';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { SensitiveDataToggle } from '@/components/sensitive-data';
import { SidebarProvider, SidebarInset } from '@/components/blocks/evo-sidebar';
import { LeftMenu } from '@/components/LeftMenu';
import { useKeyboardShortcuts, type KeyboardShortcut } from '@/hooks/use-keyboard-shortcuts';
import { CommandPalette } from '@/components/command-palette';
import { KeyboardShortcutsDialog } from '@/components/keyboard-shortcuts-dialog';
import { shouldShowSensitiveDataToggle } from '@/config/sensitive-data-pages';
import { Breadcrumbs } from '@/components/Navigation/Breadcrumbs';
import { ROUTES } from '@/routes';
import { useBulkActions } from '@/contexts/BulkActionsContext';
import { BulkActionsBar } from '@/components/bulk-actions-bar';
import { HelpButton } from '@/components/HelpDrawer';
import { getHelpContent } from '@/data/help-content';
import { HelpMenu } from '@/components/ProductTour/HelpMenu';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPage: string;
}

const navigation = [
  { id: 'dashboard', name: 'Homepage', route: ROUTES.DASHBOARD },
  { id: 'projects', name: 'Projects', route: ROUTES.PROJECTS },
  { id: 'portfolio', name: 'Portfolio', route: ROUTES.PORTFOLIO },
  { id: 'utilisation', name: 'Utilisation', route: ROUTES.UTILISATION },
  { id: 'wip-reporting', name: 'WIP', route: ROUTES.WIP_REPORTING },
  { id: 'profitability', name: 'Profitability', route: ROUTES.PROFITABILITY },

  { id: 'resources', name: 'Resource Allocation', route: ROUTES.RESOURCES },
  { id: 'project-transactions', name: 'Project Transactions', route: ROUTES.PROJECT_TRANSACTIONS },
  { id: 'gantt', name: 'Gantt Charts', route: ROUTES.GANTT },
  { id: 'settings', name: 'Settings', route: ROUTES.SETTINGS_GENERAL },
];

export function AppLayout({ children, currentPage }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const { bulkActionsState } = useBulkActions();

  // Determine active page from route
  // Settings sub-routes like /settings/general need to map to 'settings-general' for the menu
  // Reports sub-routes like /reports/budget-variance need to return just 'budget-variance' for the menu
  const activePage = (() => {
    const path = location.pathname.slice(1) || 'dashboard';
    const segments = path.split('/');
    if (segments[0] === 'settings' && segments.length > 1) {
      return `settings-${segments[1]}`;
    }
    if (segments[0] === 'reports' && segments.length > 1) {
      return segments[1];
    }
    if (segments[0] === 'projects' && segments.length > 1) {
      return 'project-details';
    }
    return path;
  })();

  // Get the display name for the current page to use as the feedback feature
  const currentPageName = navigation.find(item => item.id === currentPage)?.name ?? 'Projects';

  // Handle page change from LeftMenu
  const handlePageChange = (page: string) => {
    // Map page names to routes
    const routeMap: Record<string, string> = {
      'dashboard': ROUTES.DASHBOARD,
      'projects': ROUTES.PROJECTS,
      'portfolio': ROUTES.PORTFOLIO,
      'utilisation': ROUTES.UTILISATION,
      'wip-reporting': ROUTES.WIP_REPORTING,
      'project-status': ROUTES.PROJECT_STATUS_REPORT,
      'budget-variance': ROUTES.BUDGET_VARIANCE_REPORT,
      'project-pl': ROUTES.PROJECT_PL_REPORT,
      'project-forecast': ROUTES.PROJECT_FORECAST_REPORT,
      'profitability': ROUTES.PROFITABILITY,

      'resources': ROUTES.RESOURCES,
      'directory': ROUTES.DIRECTORY,
      'roles-rates': ROUTES.ROLES_RATES,
      'skills': ROUTES.SKILLS,
      'gantt': ROUTES.GANTT,
      'project-transactions': ROUTES.PROJECT_TRANSACTIONS,
      'settings': ROUTES.SETTINGS,
      'settings-general': ROUTES.SETTINGS_GENERAL,
      'settings-project': ROUTES.SETTINGS_PROJECT,
      'settings-user-preferences': ROUTES.SETTINGS_USER_PREFERENCES,
      'settings-integrations': ROUTES.SETTINGS_INTEGRATIONS,
      'settings-feature-labs': ROUTES.SETTINGS_FEATURE_LABS,
      'settings-user-permissions': ROUTES.SETTINGS_USER_PERMISSIONS,
      'settings-templates': ROUTES.SETTINGS_TEMPLATES,
      'settings-notifications': ROUTES.SETTINGS_NOTIFICATIONS,
      'project-templates': ROUTES.PROJECT_TEMPLATES,
    };
    
    const route = routeMap[page] || `/${page}`;
    navigate(route);
  };

  // Define keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts (Cmd/Ctrl + 1-9)
    {
      key: '1',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.DASHBOARD),
      description: 'Go to Homepage',
      category: 'navigation',
    },
    {
      key: '2',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROJECTS),
      description: 'Go to Projects',
      category: 'navigation',
    },
    {
      key: '3',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PORTFOLIO),
      description: 'Go to Portfolio',
      category: 'navigation',
    },
    {
      key: '4',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.UTILISATION),
      description: 'Go to Utilisation',
      category: 'navigation',
    },
    {
      key: '5',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.WIP_REPORTING),
      description: 'Go to WIP',
      category: 'navigation',
    },
    {
      key: '6',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROFITABILITY),
      description: 'Go to Profitability',
      category: 'navigation',
    },

    {
      key: '8',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.RESOURCES),
      description: 'Go to Resource Allocation',
      category: 'navigation',
    },
    {
      key: '9',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.GANTT),
      description: 'Go to Gantt Charts',
      category: 'navigation',
    },
    // Command palette (Cmd/Ctrl + K)
    {
      key: 'k',
      ctrlKey: true,
      metaKey: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Open command palette',
      category: 'actions',
    },
    // Settings (Cmd/Ctrl + ,)
    {
      key: ',',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.SETTINGS_GENERAL),
      description: 'Open Settings',
      category: 'actions',
    },
    // Keyboard shortcuts help (Cmd/Ctrl + /)
    {
      key: '/',
      ctrlKey: true,
      metaKey: true,
      action: () => setShortcutsDialogOpen(true),
      description: 'Show keyboard shortcuts',
      category: 'actions',
    },
  ];

  // Register keyboard shortcuts
  useKeyboardShortcuts({ shortcuts });

  // Scroll to top of main content area on route change
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Prepare shortcuts for the help dialog
  const shortcutCategories = [
    {
      title: 'Navigation',
      shortcuts: [
        { key: '1', ctrlKey: true, metaKey: true, description: 'Go to Homepage' },
        { key: '2', ctrlKey: true, metaKey: true, description: 'Go to Projects' },
        { key: '3', ctrlKey: true, metaKey: true, description: 'Go to Portfolio' },
        { key: '4', ctrlKey: true, metaKey: true, description: 'Go to Utilisation' },
        { key: '5', ctrlKey: true, metaKey: true, description: 'Go to WIP' },
        { key: '6', ctrlKey: true, metaKey: true, description: 'Go to Profitability' },

        { key: '8', ctrlKey: true, metaKey: true, description: 'Go to Resource Allocation' },
        { key: '9', ctrlKey: true, metaKey: true, description: 'Go to Gantt Charts' },
      ],
    },
    {
      title: 'Actions',
      shortcuts: [
        { key: 'k', ctrlKey: true, metaKey: true, description: 'Open command palette' },
        { key: ',', ctrlKey: true, metaKey: true, description: 'Open Settings' },
        { key: '/', ctrlKey: true, metaKey: true, description: 'Show keyboard shortcuts' },
      ],
    },
    {
      title: 'User Interface',
      shortcuts: [
        { key: 'b', ctrlKey: true, metaKey: true, description: 'Toggle sidebar' },
        { key: 'Escape', description: 'Close dialogs and modals' },
      ],
    },
  ];

  return (
    <div className="relative flex h-screen flex-col">
      {/* Sidebar + Content Area */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <SidebarProvider defaultOpen={true}>
          <LeftMenu activePage={activePage} onPageChange={handlePageChange} />
          
          <SidebarInset className="flex flex-col min-h-0 overflow-hidden">
            {/* App Runner-Bar - Fixed Top Bar */}
            <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-6 z-50">
              <div className="flex items-center gap-3">
                {/* Breadcrumbs */}
                <div className="flex-1 hidden md:block">
                  <Breadcrumbs currentPath={location.pathname} onNavigate={(path) => navigate(path)} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Demo/Live Mode Toggle */}
                <DataModeSelector />
                
                {/* Feature Feedback Collector (thumbs up/down) */}
                <FeatureFeedbackCollector feature={currentPageName} />
                
                {/* Hide Sensitive Data Toggle */}
                {shouldShowSensitiveDataToggle(currentPage) && <SensitiveDataToggle />}

                {/* Contextual Help Button — only shown when help content exists for this page */}
                {getHelpContent(activePage) && <HelpButton pageKey={activePage} />}

                {/* Tours & Help menu — always visible */}
                <HelpMenu currentPath={location.pathname} onNavigate={(path) => navigate(path)} />
              </div>
            </header>
            
            <main ref={mainRef} className="flex-1 overflow-y-auto overflow-x-hidden bg-neutral-50">
              <div className="w-full p-6">
                {children}
              </div>
            </main>
          </SidebarInset>
        </SidebarProvider>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onNavigate={(page) => navigate(`/${page}`)}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onOpenChange={setShortcutsDialogOpen}
        categories={shortcutCategories}
      />

      {/* Bulk Actions Bar — rendered at the root DOM level via context,
          outside all overflow/scroll containers. Uses absolute positioning
          relative to this root `relative` div so it always appears at the
          bottom of the viewport regardless of iframe/sandbox constraints. */}
      {bulkActionsState && bulkActionsState.selectedCount > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: '24px',
            left: 'calc(50% + 8rem)',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
        >
          <BulkActionsBar
            selectedCount={bulkActionsState.selectedCount}
            onAction={bulkActionsState.onAction}
            onClear={bulkActionsState.onClear}
          />
        </div>
      )}
    </div>
  );
}
